import { useState, useEffect } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { unitsAPI } from '@/lib/api';
import { formatDate, getStatusColor, getTodayString } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  Loader2, 
  Truck, 
  Filter,
  AlertTriangle,
  Plus,
  Pencil,
  X
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

const unitStatuses = [
  { value: 'in_yard', label: 'In Yard', description: 'Available in yard' },
  { value: 'on_rent', label: 'On Rent', description: 'Currently rented out' },
  { value: 'maintenance', label: 'Maintenance', description: 'Being repaired' },
  { value: 'retired', label: 'Retired', description: 'Out of service permanently' },
];

const unitTypes = [
  { value: 'standard', label: 'Standard' },
  { value: 'ADA', label: 'ADA Compliant' },
  { value: 'handwash', label: 'Handwash Station' },
];

export default function AdminUnits() {
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    unit_code: '',
    type: 'standard',
    status: 'in_yard',
    current_location: 'Yard',
    notes: '',
  });

  const today = getTodayString();

  useEffect(() => {
    fetchUnits();
  }, [statusFilter, typeFilter]);

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      
      const response = await unitsAPI.getAll(params);
      setUnits(response.data);
    } catch (error) {
      console.error('Error fetching units:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUnit = async () => {
    setSaving(true);
    try {
      await unitsAPI.create({
        ...formData,
        last_service_date: today,
        next_service_date: today,
      });
      toast.success('Unit added successfully');
      setShowAddDialog(false);
      resetForm();
      fetchUnits();
    } catch (error) {
      console.error('Error adding unit:', error);
      toast.error('Failed to add unit');
    } finally {
      setSaving(false);
    }
  };

  const handleEditUnit = async () => {
    if (!selectedUnit) return;
    setSaving(true);
    try {
      await unitsAPI.update(selectedUnit.id, {
        status: formData.status,
        current_location: formData.current_location,
        notes: formData.notes,
      });
      toast.success('Unit updated successfully');
      setShowEditDialog(false);
      setSelectedUnit(null);
      resetForm();
      fetchUnits();
    } catch (error) {
      console.error('Error updating unit:', error);
      toast.error('Failed to update unit');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (unit) => {
    setSelectedUnit(unit);
    setFormData({
      unit_code: unit.unit_code,
      type: unit.type,
      status: unit.status,
      current_location: unit.current_location || '',
      notes: unit.notes || '',
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      unit_code: '',
      type: 'standard',
      status: 'in_yard',
      current_location: 'Yard',
      notes: '',
    });
  };

  const isOverdue = (unit) => {
    return unit.next_service_date && unit.next_service_date < today;
  };

  const overdueCount = units.filter(isOverdue).length;
  const activeUnits = units.filter(u => u.status !== 'retired');
  const retiredUnits = units.filter(u => u.status === 'retired');

  return (
    <AdminLayout title="Units">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{activeUnits.length}</p>
            <p className="text-sm text-slate-500">Active Units</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">
              {units.filter(u => u.status === 'on_rent').length}
            </p>
            <p className="text-sm text-slate-500">On Rent</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-600">
              {units.filter(u => u.status === 'in_yard').length}
            </p>
            <p className="text-sm text-slate-500">In Yard</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">
              {units.filter(u => u.status === 'maintenance').length}
            </p>
            <p className="text-sm text-slate-500">Maintenance</p>
          </CardContent>
        </Card>
        <Card className={`border-slate-200 ${overdueCount > 0 ? 'border-red-300 bg-red-50' : ''}`}>
          <CardContent className="p-4 text-center">
            <p className={`text-2xl font-bold ${overdueCount > 0 ? 'text-red-600' : 'text-slate-600'}`}>
              {overdueCount}
            </p>
            <p className="text-sm text-slate-500">Overdue</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Add Button */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-slate-400" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="in_yard">In Yard</SelectItem>
                <SelectItem value="on_rent">On Rent</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[150px]" data-testid="type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="standard">Standard</SelectItem>
              <SelectItem value="ADA">ADA</SelectItem>
              <SelectItem value="handwash">Handwash</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button 
          onClick={() => setShowAddDialog(true)}
          className="bg-[#194461] hover:bg-[#091D2E]"
          data-testid="add-unit-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Unit
        </Button>
      </div>

      {/* Units List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#194461]" />
        </div>
      ) : units.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <Truck className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Units Found</h3>
            <p className="text-slate-600">Try adjusting your filters or add a new unit</p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Unit Code</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Type</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 hidden sm:table-cell">Location</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 hidden md:table-cell">Last Service</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Next Service</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {units.map((unit) => {
                const overdue = isOverdue(unit);
                const isRetired = unit.status === 'retired';
                return (
                  <tr 
                    key={unit.id}
                    className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                      overdue ? 'bg-red-50' : isRetired ? 'bg-slate-100 opacity-60' : ''
                    }`}
                    data-testid={`unit-row-${unit.id}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${isRetired ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                          {unit.unit_code}
                        </span>
                        {overdue && !isRetired && (
                          <AlertTriangle size={14} className="text-red-500" />
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className="bg-slate-50">
                        {unit.type}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={getStatusColor(unit.status)} variant="outline">
                        {unit.status.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 hidden sm:table-cell">
                      <span className="text-sm text-slate-600">{unit.current_location || '-'}</span>
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell">
                      <span className="text-sm text-slate-600">
                        {unit.last_service_date ? formatDate(unit.last_service_date) : '-'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-sm ${overdue ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                        {unit.next_service_date ? formatDate(unit.next_service_date) : '-'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(unit)}
                        data-testid={`edit-unit-${unit.id}`}
                      >
                        <Pencil size={14} className="mr-1" />
                        Edit
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Unit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Unit</DialogTitle>
            <DialogDescription>
              Add a new portable toilet unit to your inventory
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="unit_code">Unit Code *</Label>
              <Input
                id="unit_code"
                value={formData.unit_code}
                onChange={(e) => setFormData(prev => ({ ...prev, unit_code: e.target.value }))}
                placeholder="TT-041"
                data-testid="input-unit-code"
              />
              <p className="text-xs text-slate-500 mt-1">Use format: TT-XXX for standard, ADA-XXX for ADA, HW-XXX for handwash</p>
            </div>

            <div>
              <Label htmlFor="type">Unit Type *</Label>
              <Select 
                value={formData.type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger data-testid="select-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {unitTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="status">Initial Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger data-testid="select-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {unitStatuses.filter(s => s.value !== 'retired').map(status => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="current_location">Current Location</Label>
              <Input
                id="current_location"
                value={formData.current_location}
                onChange={(e) => setFormData(prev => ({ ...prev, current_location: e.target.value }))}
                placeholder="Yard"
                data-testid="input-location"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any notes about this unit..."
                rows={2}
                data-testid="input-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAddUnit}
              disabled={saving || !formData.unit_code}
              className="bg-[#194461] hover:bg-[#091D2E]"
              data-testid="save-unit-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Unit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Unit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Unit: {selectedUnit?.unit_code}</DialogTitle>
            <DialogDescription>
              Update unit status and information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Unit Code</Label>
              <p className="text-lg font-semibold text-slate-900">{selectedUnit?.unit_code}</p>
            </div>

            <div>
              <Label>Type</Label>
              <p className="text-slate-600">{selectedUnit?.type}</p>
            </div>

            <div>
              <Label htmlFor="edit-status">Status *</Label>
              <Select 
                value={formData.status} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger data-testid="edit-select-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {unitStatuses.map(status => (
                    <SelectItem key={status.value} value={status.value}>
                      <div>
                        <span className="font-medium">{status.label}</span>
                        <span className="text-slate-500 text-xs ml-2">({status.description})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.status === 'retired' && (
                <p className="text-xs text-red-600 mt-1">
                  Warning: Retiring a unit removes it from active service permanently.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="edit-location">Current Location</Label>
              <Input
                id="edit-location"
                value={formData.current_location}
                onChange={(e) => setFormData(prev => ({ ...prev, current_location: e.target.value }))}
                placeholder="Yard"
                data-testid="edit-input-location"
              />
            </div>

            <div>
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any notes about this unit..."
                rows={2}
                data-testid="edit-input-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleEditUnit}
              disabled={saving}
              className="bg-[#194461] hover:bg-[#091D2E]"
              data-testid="update-unit-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update Unit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
